import type { Dispatch, RefObject, SetStateAction } from 'react';
import { useCallback, useRef } from 'react';
import type { ChatRequest } from '../../../../app/api/chat/types';
import type { StreamingLogger } from '../../chat-helpers';
import type { Message } from '../../chat-types';
import type { ProcessStreamLine, StreamingSubscriptionHandlers } from './types';

interface UseStreamingSubscriptionOptions {
  handlers?: StreamingSubscriptionHandlers;
  log: StreamingLogger;
  messagesRef: RefObject<Message[]>;
  postPath: string;
  processStreamLine: ProcessStreamLine;
  setMessages: Dispatch<SetStateAction<Message[]>>;
}

const decodeChunk = (decoder: TextDecoder, chunk: Uint8Array) => decoder.decode(chunk, { stream: true });

export const useStreamingSubscription = ({
  log,
  messagesRef,
  setMessages,
  processStreamLine,
  handlers,
  postPath,
}: UseStreamingSubscriptionOptions) => {
  const streamStatsReference = useRef({ chunks: 0, lines: 0, bytes: 0 });

  const readStream = useCallback(
    async (response: Response, assistantId: string) => {
      log.info('Reading chat stream');
      const reader = response.body?.getReader();
      if (!reader) {
        log.error('No readable stream on response');
        return;
      }
      const decoder = new TextDecoder();
      let buffer = '';
      streamStatsReference.current = { chunks: 0, lines: 0, bytes: 0 };

      for (let chunk = await reader.read(); !chunk.done; chunk = await reader.read()) {
        const value = chunk.value ?? new Uint8Array();
        streamStatsReference.current.chunks += 1;
        streamStatsReference.current.bytes += value.byteLength;
        buffer += decodeChunk(decoder, value);
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          processStreamLine(line, assistantId);
          streamStatsReference.current.lines += 1;
        }
      }

      const flushRemainder = decoder.decode();
      if (flushRemainder) {
        buffer += flushRemainder;
      }
      if (buffer.length > 0) {
        const trailingLines = buffer.split('\n');
        for (const line of trailingLines) {
          if (!line.trim()) {
            continue;
          }
          processStreamLine(line, assistantId);
          streamStatsReference.current.lines += 1;
        }
        buffer = '';
      }

      const finalMessage = messagesRef.current.find((message) => message.id === assistantId);
      setMessages((previous) =>
        previous.map((message) => (message.id === assistantId ? { ...message, status: 'complete' } : message))
      );

      handlers?.onStreamComplete?.({
        assistantId,
        stats: {
          ...streamStatsReference.current,
          assistantContentLength: finalMessage?.content?.length ?? 0,
        },
      });
    },
    [handlers, log, messagesRef, processStreamLine, setMessages]
  );

  const postChat = useCallback(
    (payload: ChatRequest, signal: AbortSignal): Promise<Response> =>
      fetch(postPath, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
        },
        body: JSON.stringify(payload),
        signal,
      }),
    [postPath]
  );

  return { readStream, postChat };
};
